import React, {useEffect, useRef, useState} from 'react';
import ArrModal, {ArrModalProps} from '../../common/arr-modal/ArrModal';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './BulkTaggingWarning.scss';
import {isEmpty} from 'lodash';
import {IActionListItem} from '../action-like-documents/ActionLikeDocuments';
import {
  IGetTaggingConflictDataRes,
  ISaveBulkTagReportDataRes,
} from '../../../api/types/tagFoldering.type';
import ArrBarChart from '../../common/arr-bar-chart/ArrBarChart';
import {
  getBulkTagTallyReportStatus,
  getTaggingConflictData,
  saveBulkTagReportData,
  superMasterDocListGen,
} from '../../../api/endpoints/tagsFolderingApi';
import ArrButton from '../../common/arr-button/ArrButton';
import {useNavigate} from 'react-router-dom';
import {SourceTypeCode} from '../../../shared/constants';

// Types Definitions
// ----------------------------------
interface BulkTaggingWarningProps extends ArrModalProps {
  sourceType: string;
  actionLikeChangeList?: IActionListItem[];
  masterDocListId: number;
  onFooterClick?: () => void;
  selectedNewExistingTag?: string[];
}
// ----------------

interface BulkTaggingFooterProps {
  isLoading: boolean;
  onClick?: () => void;
}

const errorCode = '17001000001';
const MAX_RETRIES = 2;

// Modal Footer
// ----------------------------------
export const BulkTaggingFooter: React.FC<BulkTaggingFooterProps> = ({
  isLoading,
  onClick,
}) => (
  <Box className="bulk-warning__footer" data-testid="bulk-warning-footer">
    <ArrButton
      variant="text"
      size="medium"
      disabled={isLoading}
      onClick={onClick}
      dataTestId="bulk-warning-footer-button"
      tabIndex={0}
    >
      {t('docExplorer.actionMenu.bulkTaggingWarning.tallyButtonText')}
    </ArrButton>
  </Box>
);

// ----------------------------------
// BulkTaggingWarning Component
// ----------------------------------
const BulkTaggingWarning: React.FC<BulkTaggingWarningProps> = ({
  title = t('docExplorer.actionMenu.bulkTaggingWarning.title'),
  description = t('docExplorer.actionMenu.bulkTaggingWarning.description'),
  showModal = false,
  onClose,
  sourceType,
  actionLikeChangeList,
  masterDocListId,
  selectedNewExistingTag,
  dataTestId,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [sMasterDocListId, setSMasterDocListId] =
    useState<number>(masterDocListId);
  // Ref to always hold the latest masterDocListId
  const sMasterDocListIdRef = useRef<number>(masterDocListId);
  useEffect(() => {
    sMasterDocListIdRef.current = sMasterDocListId;
  }, [sMasterDocListId]);

  const [saveBulkTagReportDataRes, setSaveBulkTagReportDataRes] =
    useState<ISaveBulkTagReportDataRes>();
  const saveBulkTagReportDataResRef = useRef<
    ISaveBulkTagReportDataRes | undefined
  >(saveBulkTagReportDataRes);
  useEffect(() => {
    saveBulkTagReportDataResRef.current = saveBulkTagReportDataRes;
  }, [saveBulkTagReportDataRes]);
  const [conflictResData, setConflictResData] =
    useState<IGetTaggingConflictDataRes>();

  // 2nd API: getBulkTagTallyReportStatus
  const getReportStatus = (
    jobId: string,
    flagValue: string,
    retryCount: number = 0
  ) => {
    getBulkTagTallyReportStatus({jobId})
      .then((res) => {
        if (res.jobStatus >= 4) {
          if (retryCount < MAX_RETRIES) {
            setTimeout(() => {
              getReportStatus(jobId, flagValue, retryCount + 1);
            }, 3000);
          } else {
            // STOP loop — fail gracefully
            setIsLoading(false);
          }
        } else if (res.jobStatus === 1) {
          if (flagValue === 'superMasterDocList') {
            //  3rd API: saveBulkTagReportData
            saveBulkTagReportDataCall();
          } else if (flagValue === 'saveBulkTagReport') {
            //  4th API: getTaggingConflictData
            getTaggingConflictData(getTaggingConflictDataPayload()).then(
              (res) => {
                setConflictResData(res);
                setIsLoading(false);
              }
            );
          }
        }
      })
      .catch((e) => {
        setIsLoading(false);
        console.log(e);
      });
  };

  // 1st API: superMasterDocListGen
  /**
   *
   * @returns superMasterDocListGen Payload
   */
  const getSuperMDocListGenPayload = () => {
    let selectionData = {
      isFamilyMemberIncluded: false,
      isEmailThreadsIncluded: false,
      isNearDupesIncluded: false,
    };
    actionLikeChangeList?.map((i) => {
      if (i.selected) {
        if (i.id === 'family_members') {
          selectionData = {...selectionData, isFamilyMemberIncluded: true};
        } else if (i.id === 'email_docs') {
          selectionData = {...selectionData, isEmailThreadsIncluded: true};
        } else if (i.id === 'near_duplicates') {
          selectionData = {...selectionData, isNearDupesIncluded: true};
        }
      }
    });
    const {
      isNearDupesIncluded,
      isEmailThreadsIncluded,
      isFamilyMemberIncluded,
    } = selectionData;
    const payload = {
      selectionDetails: {
        isNearDupesIncluded,
        isEmailThreadsIncluded,
        isFamilyMemberIncluded,
        sourceContextTypeCode: sourceType,
        reviewerUserID: 0,
      },
      masterDocListId,
    };
    return payload;
  };

  const enabledSuperMDocList =
    actionLikeChangeList?.some((i) => i.selected) ||
    sourceType === 'ASSIGNMENT' ||
    sourceType === 'ASSIGNMENTREVIEWER' ||
    false;
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return; // skip second mount
    didMountRef.current = true;

    if (enabledSuperMDocList) {
      try {
        superMasterDocListGen(getSuperMDocListGenPayload()).then((res) => {
          const {sMasterDocListId, jobId} = res;
          setSMasterDocListId(Number(sMasterDocListId));
          getReportStatus(jobId, 'superMasterDocList');
        });
      } catch (e) {
        setIsLoading(false);
        console.log(e);
      }
    } else {
      // 3rd API(saveBulkTagReportData) call
      saveBulkTagReportDataCall();
    }
  }, []);

  // 3rd API(saveBulkTagReportData) Payload
  const saveBulkTagReportDataCall = () => {
    const payload = {
      masterDocListId: sMasterDocListIdRef.current,
      selectedTags:
        (!isEmpty(selectedNewExistingTag) &&
          selectedNewExistingTag?.map(Number)) ||
        [],
    };

    saveBulkTagReportData(payload)
      .then((res) => {
        setSaveBulkTagReportDataRes(res);
        if (res.errorMessage.includes(errorCode)) {
          getTaggingConflictData(getTaggingConflictDataPayload()).then(
            (res) => {
              setConflictResData(res);
              setIsLoading(false);
            }
          );
        } else if (res.jobId) getReportStatus(res.jobId, 'saveBulkTagReport');
      })
      .catch((e) => {
        setIsLoading(false);
        console.log(e);
      });
  };

  // 4th API(getTaggingConflictData) payload
  const getTaggingConflictDataPayload = () => {
    const res = saveBulkTagReportDataResRef.current;
    if (!res)
      return {MasterDocListId: 0, ReportId: 0, FilePath: '', FileName: ''};

    const {reportId, filePath, fileName} = res;
    return {
      MasterDocListId: sMasterDocListIdRef.current,
      ReportId: reportId,
      FilePath: filePath,
      FileName: fileName,
    };
  };

  /**
   *
   * @returns return Bar-chart acceptable's obj format
   */
  const modifiedConflictResData = () => {
    let dataObj: {label: string; value: number}[] = [];
    conflictResData?.taggingConflictData?.map((i) => {
      dataObj = [...dataObj, {label: i.fieldValue, value: i.docCount}];
    });
    return dataObj;
  };

  const handleFooterClick = () => {
    const reportId = saveBulkTagReportDataResRef.current?.reportId ?? 0;
    navigate(`/Report/Tally${reportId > 0 ? `?ReportID=${reportId}` : ''}`, {
      state: {
        navigationSourceType: SourceTypeCode.BULKTAG,
        source: [String(masterDocListId)],
        sourceName: SourceTypeCode.BULKTAG,
      },
    });
  };

  return (
    <ArrModal
      headerTitle={t('docExplorer.actionMenu.bulkTaggingWarning.headerTitle')}
      headerTitleClassName={'modal-header-title'}
      title={title}
      description={description}
      showModal={showModal}
      showClose={true}
      autoFocusCloseButton={true}
      onClose={onClose}
      className="bulk-warning"
      footer={
        <BulkTaggingFooter isLoading={isLoading} onClick={handleFooterClick} />
      }
      dataTestId="bulk-tagging-warning"
      {...props}
    >
      <Box
        className="bulk-warning__wrapper"
        data-testid="bulk-warning-warning-wrapper"
        tabIndex={0}
        aria-label={t('docExplorer.actionMenu.bulkTaggingWarning.title')}
      >
        {isLoading ? (
          <Box
            className="bulk-warning__content"
            data-testid="bulk-warning-warning-spinner-content"
          >
            <ArrLoadingSpinner
              size={32}
              data-testid="bulk-warning-warning-spinner"
            />
          </Box>
        ) : (
          <Box
            className="bulk-warning__content"
            data-testid="bulk-warning-warning-content"
          >
            {isEmpty(conflictResData?.taggingConflictData) ? (
              <Typography
                component="p"
                tabIndex={0} //make focusable
                aria-label={t(
                  'docExplorer.actionMenu.bulkTaggingWarning.defaultText'
                )}
                className="typo-text body4_size"
                data-testid="bulk-warning-warning-default-text"
              >
                {t('docExplorer.actionMenu.bulkTaggingWarning.defaultText')}
              </Typography>
            ) : (
              <ArrBarChart
                data={modifiedConflictResData()}
                xLabel={t(
                  'docExplorer.actionMenu.bulkTaggingWarning.chartLabel'
                )}
                dataTestId="bulk-warning-bar-chart"
              />
            )}
          </Box>
        )}
      </Box>
    </ArrModal>
  );
};

export {BulkTaggingWarning};
export default React.memo(BulkTaggingWarning);
